import {
	Box,
	DialogContent,
	Menu,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from '@mui/material';
import { FC, SyntheticEvent, useState, useRef } from 'react';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { OrderCurrency } from 'ui/OrderCurrency';

export type TAmountSpeedDialProps = {
	easFee?: number;
	monthlyFee?: number;
	invoicingFee?: number;
	vatOnEasFeeAndInvoicingFee?: number;
	vatRate?: number;
	vatDueToEas?: number;
	delayedPaymentFee?: number;
	invoiceCarryOverAmount?: number;
	total?: number;
	delayedInvoiceNumber?: number;
	customsDuties?: number;
	easCustomsFee?: number;
	customsVat?: number;
	reimbursedCountries?: string;
	reimbursedAmount?: number;
};

export const AmountSpeedDial: FC<TAmountSpeedDialProps> = ({
	easFee = 0,
	monthlyFee = 0,
	invoicingFee = 0,
	vatOnEasFeeAndInvoicingFee = 0,
	vatRate = 0,
	vatDueToEas = 0,
	delayedPaymentFee = 0,
	invoiceCarryOverAmount = 0,
	total = 0,
	delayedInvoiceNumber = 0,
	customsDuties = 0,
	customsVat = 0,
	easCustomsFee = 0,
	reimbursedCountries = null,
	reimbursedAmount = 0,
}) => {
	const anchorInvoiceEl = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);

	const orderCurrency = 'EUR';

	const periodTotal = Math.round((total - invoiceCarryOverAmount) * 100) / 100;

	const speedDialTooltipOpen = (e: SyntheticEvent) => {
		e.stopPropagation();
		setOpen(true);
	};
	const speedDialTooltipClose = (e: SyntheticEvent) => {
		e.stopPropagation();
		setOpen(false);
	};

	return (
		<>
			<div
				onClick={speedDialTooltipOpen}
				style={{ position: 'absolute', right: '0', top: '0', width: '100%', height: '100%' }}
				className="action-speed-dial"
				ref={anchorInvoiceEl}
			/>
			<Menu
				anchorEl={anchorInvoiceEl.current}
				open={open}
				onClose={speedDialTooltipClose}
				sx={{
					'& .MuiPaper-root': {
						width: '400px',
						maxWidth: 'none',
						borderRadius: '12px',
						p: 0,
						m: 0,
					},
					'& .MuiList-root': {
						padding: 0,
						m: 0,
					},
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
			>
				<Box>
					{/* <DialogTitle color="primary" sx={{ p: '10px', fontSize: '1rem' }}>
						Amount breakdown
					</DialogTitle> */}
					<DialogContent sx={{ p: '10px' }}>
						<TableContainer>
							<Table>
								<TableBody>
									{!!easFee && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												EAS fee for order processing:
											</TableCell>
											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency amount={easFee ?? 0} currency={orderCurrency} />
											</TableCell>
										</TableRow>
									)}
									{!!monthlyFee && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>Monthly fee:</TableCell>
											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency amount={monthlyFee ?? 0} currency={orderCurrency} />
											</TableCell>
										</TableRow>
									)}
									{!!invoicingFee && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												Invoicing fee:
												<div
													style={{
														fontStyle: 'italic',
														color: '#888',
														fontSize: '.8rem',
														margin: '-2px 0 5px 10px',
													}}
												>
													Invoicing fee applied for the orders with total amount greater than 30 EUR
												</div>
											</TableCell>
											<TableCell
												align="right"
												sx={{ padding: '2px', borderBottom: 0, verticalAlign: 'top' }}
											>
												<AmountAndCurrency amount={invoicingFee ?? 0} currency={orderCurrency} />
											</TableCell>
										</TableRow>
									)}
									{!!vatOnEasFeeAndInvoicingFee && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												VAT amount on fees ({vatRate}%):
												<div
													style={{
														fontStyle: 'italic',
														color: '#888',
														fontSize: '.8rem',
														margin: '-2px 0 5px 10px',
													}}
												>
													See EAS VAT number on the PDF Invoice
												</div>
											</TableCell>
											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency
													amount={vatOnEasFeeAndInvoicingFee ?? 0}
													currency={orderCurrency}
												/>
											</TableCell>
										</TableRow>
									)}
									{!!vatDueToEas && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												VAT due to EAS:
												<div
													style={{
														fontStyle: 'italic',
														color: '#888',
														fontSize: '.8rem',
														margin: '-2px 0 5px 10px',
													}}
												>
													Will be transferred to EU authorities
												</div>
											</TableCell>
											<TableCell
												align="right"
												sx={{ padding: '2px', borderBottom: 0, verticalAlign: 'top' }}
											>
												<AmountAndCurrency amount={vatDueToEas ?? 0} currency={orderCurrency} />
											</TableCell>
										</TableRow>
									)}
									{!!delayedPaymentFee && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												Penalties for unpaid invoice #{delayedInvoiceNumber}:
											</TableCell>
											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency
													amount={delayedPaymentFee ?? 0}
													currency={orderCurrency}
												/>
											</TableCell>
										</TableRow>
									)}
									{!!reimbursedCountries && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												Reimbursed VAT:
												<div
													style={{
														fontStyle: 'italic',
														color: '#888',
														fontSize: '.8rem',
														margin: '-2px 0 5px 10px',
													}}
												>
													from EU Tax authorities for previous periods ({reimbursedCountries})
												</div>
											</TableCell>
											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency
													amount={reimbursedAmount ?? 0}
													currency={orderCurrency}
												/>
											</TableCell>
										</TableRow>
									)}
									{!!customsDuties && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												DDP Customs duties:
											</TableCell>
											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency amount={customsDuties ?? 0} currency={orderCurrency} />
											</TableCell>
										</TableRow>
									)}
									{!!customsVat && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												DDP Value Added Tax:
											</TableCell>
											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency amount={customsVat ?? 0} currency={orderCurrency} />
											</TableCell>
										</TableRow>
									)}
									{!!easCustomsFee && (
										<TableRow>
											<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
												Logistics Handling Fee:
											</TableCell>
											<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
												<AmountAndCurrency amount={easCustomsFee ?? 0} currency={orderCurrency} />
											</TableCell>
										</TableRow>
									)}
									{!!invoiceCarryOverAmount && (
										<>
											<TableRow>
												<TableCell
													color="primary"
													sx={{
														padding: '6px 2px 2px 2px',
														borderBottom: 0,
														borderTop: '1px solid #016193',
														fontWeight: 'bold',
														color: '#016193',
													}}
												>
													Invoiced for the period:
												</TableCell>
												<TableCell
													color="primary"
													align="right"
													sx={{
														padding: '6px 2px 2px 2px',
														borderBottom: 0,
														borderTop: '1px solid #d5d5d5',
														fontWeight: 'bold',
													}}
												>
													<AmountAndCurrency
														amount={periodTotal}
														currency={orderCurrency}
														bold
														color="primary"
													/>
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell
													sx={{
														padding: '6px 2px 2px 2px',
														borderBottom: 0,
														fontWeight: 'bold',
														color: '#016193',
													}}
													color="primary"
												>
													{invoiceCarryOverAmount < 0 ? 'Overpayment' : 'Underpayment'} from
													previous invoice:
												</TableCell>
												<TableCell
													align="right"
													sx={{ padding: '6px 2px 2px 2px', borderBottom: 0, fontWeight: 'bold' }}
												>
													<AmountAndCurrency
														amount={invoiceCarryOverAmount ?? 0}
														currency={orderCurrency}
														bold
														color="primary"
													/>
												</TableCell>
											</TableRow>
										</>
									)}

									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
									</TableRow>
									<TableRow>
										<TableCell
											sx={{
												padding: '4px 2px 2px 2px',

												borderBottom: 0,
												borderTop: '1px solid #016193',
											}}
										>
											<Typography color="primary" sx={{ fontWeight: 'bold' }}>
												Total:
											</Typography>
										</TableCell>
										<TableCell
											align="right"
											sx={{
												padding: '8px 4px 4px 4px',
												mt: '8px',
												fontWeight: 'bold',
												borderBottom: 0,
												borderTop: '1px solid #016193',
											}}
										>
											<AmountAndCurrency
												amount={total ?? 0}
												currency={orderCurrency}
												color="primary"
												bold
											/>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
					</DialogContent>
				</Box>
			</Menu>
		</>
	);
};
